import { useEffect, useRef, useState } from "react";
import { WorldRenderer } from "./WorldRenderer";
import { Box, CssBaseline, Typography, useTheme } from "@mui/material";
import { WorldPropertiesPanel } from "./worldpanelparts/WorldPropertiesPanel";
import { SceneInfoOverlay } from "./worldpanelparts/overlays/SceneInfoOverlay";
import { WREvents } from "./WREvents";
import { PlacesSearch } from "./worldpanelparts/PlacesSearch";
import { ModelSelectBar } from "./common/ModelSelectBar";
import { loadOrgRecords } from "../api/connection";
import { createModel, Model } from "../models/Model";
import * as THREE from "three";
import { createNewVisualFromModel } from "../visuals/builder";
import { getIntristicModels } from "../visuals/IntristicVisualModel";
import { FloorEditor } from "./worldpanelparts/overlays/FloorEditor";
import PanelToggles from "../components/PanelToggles";
import { AgentControl } from "./worldpanelparts/overlays/AgentControl";
import { ScenarioEditor } from "./worldpanelparts/overlays/ScenarioEditor";
import { CameraOverlay } from "./worldpanelparts/overlays/CameraOverlay";

const panelConfig = {
  rightWidth: 400,
  leftWidth: 200,
  bottomHeight: 150,
};

export default function WorldEditPanel({ heightOffset = 0, activeSiteId = '', rendererKey }: { heightOffset?: number, activeSiteId: string, rendererKey?: string }) {
  const divRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  const [boxPosition, setBoxPosition] = useState({ x: 0, y: 0 });
  const [renderer, setRenderer] = useState<WorldRenderer | null>(null);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [bottomOpen, setBottomOpen] = useState(false);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedModel, setSelectedModel] = useState<Model>();
  const theme = useTheme();
  useEffect(() => {
    if (divRef.current && !renderer) {
      const newRenderer = new WorldRenderer(divRef.current);
      setRenderer(newRenderer);

      newRenderer.onEvent(WREvents.ModelDeploy, async (model: Model, point: THREE.Vector3) => {
        setSelectedModel(undefined);
        await createNewVisualFromModel(newRenderer, model, point);
      });

      // Debounced resize handler - simplified logic
      let debounceTimeout: NodeJS.Timeout | null = null;
      const windowResizeHandler = () => {
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
        debounceTimeout = setTimeout(() => {
          if (boxRef.current) {
            const rect = boxRef.current.getBoundingClientRect();
            setBoxPosition({ x: rect.left, y: rect.top });
            setBoxSize({
              width: boxRef.current.clientWidth,
              height: boxRef.current.clientHeight,
            });
          }
        }, 200); // Reduced from 700ms for better responsiveness
      };

      window.addEventListener('resize', windowResizeHandler);

      // Initial size calculation
      if (boxRef.current) {
        const rect = boxRef.current.getBoundingClientRect();
        setBoxSize({
          width: boxRef.current.clientWidth,
          height: boxRef.current.clientHeight,
        });
        setBoxPosition({ x: rect.left, y: rect.top });
      }

      // Load models
      (async () => {
        const ms = await loadOrgRecords('models', createModel);
        const intristicModels = getIntristicModels();
        const allModels = [...ms, ...intristicModels];
        allModels.sort((a, b) => `${a.category}_${a.name}`.localeCompare(`${b.category}_${b.name}`));
        setModels(allModels);
      })();

      return () => {
        window.removeEventListener('resize', windowResizeHandler);
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
      };
    }
  }, [rendererKey, divRef]);
  useEffect(() => {
    let checksRemaining = 20;
    const interval = setInterval(async () => {
      if (checksRemaining-- <= 0) {
        clearInterval(interval);
        return;
      }
      if (!renderer) {
        return;
      }
      if (activeSiteId && !renderer.dataService.sites.has(activeSiteId)) {
        return;
      }
      clearInterval(interval);
      if (activeSiteId) {
        setTimeout(async () => {
          await renderer.dataService.activateSite(activeSiteId);
          const site = renderer.dataService.activeSite!;
          renderer.emitEvent(WREvents.VisualSelected, site);
          if (!site.settings.cameraPosition) {
            return;
          }
          const p = site.settings.cameraPosition!.position;
          let relativeToSite = new THREE.Vector3(p.x, p.y, p.z);
          relativeToSite = site.obj.worldToLocal(relativeToSite);
          renderer.setCameraPosition(site.settings.cameraPosition!);
        }, 3000);
      } else {
        renderer.planetService.pointCameraTo({
          lat: 37.7749,
          lon: -122.4194,
          alt: 5000
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activeSiteId, renderer]);

  // Update renderer size when layout changes
  useEffect(() => {
    if (!renderer || !boxRef.current) return;

    // Calculate the target size immediately based on panel states
    const calculateTargetSize = () => {
      const containerWidth = window.innerWidth;
      const leftPanelWidth = leftOpen ? panelConfig.leftWidth : 0;
      const rightPanelWidth = rightOpen ? panelConfig.rightWidth : 0;
      const targetWidth = containerWidth - leftPanelWidth - rightPanelWidth;

      const containerHeight = window.innerHeight - heightOffset;
      const bottomPanelHeight = bottomOpen ? panelConfig.bottomHeight : 0;
      const targetHeight = containerHeight - bottomPanelHeight;

      return { width: Math.max(0, targetWidth), height: Math.max(0, targetHeight) };
    };

    const targetSize = calculateTargetSize();

    // Immediately update the Three.js renderer to the target size to prevent glitches
    if (targetSize.width > 0 && targetSize.height > 0) {
      try {
        renderer.setSize(targetSize.width, targetSize.height);
      } catch (error) {
        console.warn('Immediate renderer resize failed:', error);
      }
    }

    // Update React state after the CSS transition completes
    const timeout = setTimeout(() => {
      if (boxRef.current) {
        const actualWidth = boxRef.current.clientWidth;
        const actualHeight = boxRef.current.clientHeight;

        setBoxSize({ width: actualWidth, height: actualHeight });
        const rect = boxRef.current.getBoundingClientRect();
        setBoxPosition({ x: rect.left, y: rect.top });

        // Final sync to ensure accuracy
        if (actualWidth > 0 && actualHeight > 0) {
          try {
            renderer.setSize(actualWidth, actualHeight);
          } catch (error) {
            console.warn('Final renderer resize failed:', error);
          }
        }
      }
    }, 320);

    return () => clearTimeout(timeout);
  }, [rightOpen, leftOpen, bottomOpen, renderer, heightOffset]);

  // Handle window resize separately - keep this simpler since it's not a transition
  useEffect(() => {
    if (!renderer || !boxRef.current) return;

    let debounceTimeout: NodeJS.Timeout | null = null;

    const handleResize = () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      debounceTimeout = setTimeout(() => {
        if (boxRef.current && renderer) {
          const width = boxRef.current.clientWidth;
          const height = boxRef.current.clientHeight;

          if (width > 0 && height > 0) {
            setBoxSize({ width, height });
            const rect = boxRef.current.getBoundingClientRect();
            setBoxPosition({ x: rect.left, y: rect.top });

            try {
              renderer.setSize(width, height);
            } catch (error) {
              console.warn('Window resize failed:', error);
            }
          }
        }
      }, 150);
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(boxRef.current);

    return () => {
      resizeObserver.disconnect();
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [renderer]);

  const handleLeftToggle = () => setLeftOpen(!leftOpen);
  const handleRightToggle = () => setRightOpen(!rightOpen);
  const handleBottomToggle = () => setBottomOpen(!bottomOpen);

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <CssBaseline />
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: `${heightOffset}px`,
        flexShrink: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,

      }}>
        <Box sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: 64,
        }}>
          <Typography
            sx={{ paddingLeft: '24px', fontSize: '20px', }}
            variant="h6"
          >
            Digital World
          </Typography>
        </Box>
        <PlacesSearch wr={renderer!} />
        <PanelToggles
          leftOpen={leftOpen}
          rightOpen={rightOpen}
          bottomOpen={bottomOpen}
          onLeftToggle={handleLeftToggle}
          onRightToggle={handleRightToggle}
          onBottomToggle={handleBottomToggle}
        />
      </Box>

      <Box sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',

      }}>

        <Box sx={{
          width: leftOpen ? panelConfig.leftWidth : 0,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width 0.3s ease',
        }}>
          <ModelSelectBar
            models={models}
            selectedModelId={selectedModel?._id || ''}
            onModelSelect={(model) => {
              setSelectedModel(model);
              renderer!.userEventer.setSelectedModel(model);
            }}
          />
        </Box>


        <Box
          ref={boxRef}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            ref={divRef}
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              padding: 0,
              margin: 0,
            }}
            tabIndex={0}
          />
          {renderer && <CameraOverlay wr={renderer} size={boxSize} position={{ x: 0, y: 0 }} />}
          {renderer && <ScenarioEditor wr={renderer} size={boxSize} position={{ x: 0, y: 0 }} />}
          {renderer && <FloorEditor wr={renderer} size={boxSize} position={{ x: 0, y: 0 }} />}
          {renderer && <SceneInfoOverlay wr={renderer} size={boxSize} position={{ x: 0, y: 0 }} />}
          {renderer && <AgentControl wr={renderer} size={boxSize} position={{ x: 0, y: 0 }} />}
        </Box>


        <Box sx={{
          width: rightOpen ? panelConfig.rightWidth : 0,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'width 0.3s ease',
        }}>
          {renderer && <WorldPropertiesPanel wr={renderer} />}
        </Box>
      </Box>
      <Box sx={{
        height: bottomOpen ? panelConfig.bottomHeight : 0,
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'height 0.3s ease',
      }}>
        {/* {renderer && <ScenarioList wr={renderer} />} */}
      </Box>
    </Box>
  );
}